import Button from '@amzn/meridian/button';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React from 'react';
import Icon from '@amzn/meridian/icon';
import thumbsUpTokens from '@amzn/meridian-tokens/base/icon/thumbs-up';
import thumbsDownTokens from '@amzn/meridian-tokens/base/icon/thumbs-down';
import { blue500Element } from '../styles';
import { useToaster } from '../../../Util/AppToaster';
import { FormattedMessage, useIntl } from 'react-intl';
import useInspireOptions from '../../../hooks/useInspireOptions';
import { WeeklySummary } from '@amzn/aet-summaries-kit';
import { useMutation } from '@apollo/client';
import * as mutations from '../../../api/Summaries/mutations';
import { SubmitCSAFeedbackResponse } from '../../../api/Summaries/mutations';

interface CSAFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  summary: WeeklySummary;
}

const CSAFeedbackModal = ({
  isOpen,
  summary,
  onClose,
  onSuccess,
}: CSAFeedbackModalProps) => {
  const intl = useIntl();
  const { requesterUsername } = useInspireOptions();
  const { openToast } = useToaster();

  const [submitCSASummariesCSAFeedback, { loading }] = useMutation(
    mutations.submitCSASummariesCSAFeedback,
    {
      onCompleted: resp => handleComplete(resp),
      onError: () => handleError(),
    }
  );

  const handleFeedback = async (isGood: boolean) => {
    submitCSASummariesCSAFeedback({
      variables: {
        alias: requesterUsername,
        date: summary.date,
        summaryUUID: summary.uuid,
        isSummaryGood: isGood,
      },
    });
  };

  const handleComplete = (resp: SubmitCSAFeedbackResponse) => {
    const { error } = resp.submitCSASummariesCSAFeedback;
    if (error) return handleError();

    openToast({
      children: (
        <FormattedMessage
          id='cs_perfportal_csa_submit_summaries_feedback_success'
          defaultMessage='<bold>Response received.</bold> Thanks for your input.'
          values={{
            bold: chunks => <b>{chunks}</b>,
          }}
        />
      ),
      alertType: 'success',
    });

    onSuccess();
    onClose();
  };

  const handleError = () => {
    openToast({
      children: (
        <FormattedMessage
          id='cs_perfportal_csa_submit_summaries_feedback_error'
          defaultMessage="<bold>There was an issue saving your response.</bold> To try again, select I've read my Weekly highlights."
          values={{
            bold: chunks => <b>{chunks}</b>,
          }}
        />
      ),
      alertType: 'error',
    });
    onClose();
  };

  return (
    <Modal
      title={intl.formatMessage({
        id: 'cs_perfportal_csa_submit_summaries_feedback_title',
        defaultMessage: 'Feedback on Weekly highlights',
      })}
      open={isOpen}
      scrollContainer='modal'
      closeLabel='Close'
      aria-describedby='modal-description'
    >
      <Text>
        <FormattedMessage
          id='cs_perfportal_csa_submit_summaries_feedback_description'
          defaultMessage='Is the information in the Weekly highlights useful?'
        />
      </Text>
      <ModalFooter>
        <Row alignmentHorizontal='center' widths='fit'>
          <Button
            type='icon'
            disabled={loading}
            onClick={() => handleFeedback(true)}
            data-testid='feedback-thumbs-up'
          >
            <Icon tokens={thumbsUpTokens} className={blue500Element} />
          </Button>
          <Button
            type='icon'
            disabled={loading}
            onClick={() => handleFeedback(false)}
            data-testid='feedback-thumbs-down'
          >
            <Icon tokens={thumbsDownTokens} className={blue500Element} />
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default CSAFeedbackModal;
